<template>
    <div class="d-flex flex-column gap-6">
        <!-- Header & Filters -->
        <v-row align="end" justify="space-between" class="mb-6">
            <v-col cols="12" md="8">
                <div id="tour-dashboard-title" class="d-flex align-center mb-2">
                    <h1 class="text-h3 font-weight-bold text--primary mr-2">
                        {{ greeting }}, {{ userFirstName }}
                    </h1>
                </div>
                <p class="text-h6 text--secondary mb-0 font-weight-regular">
                    {{ formattedDate }}
                </p>
            </v-col>
            <v-col cols="12" md="auto">
                <div class="d-flex align-center gap-4">
                    <v-select v-model="month" :items="month_list" label="Mês" outlined dense hide-details
                        class="rounded-lg bg-surface" style="width: 160px"></v-select>
                    <v-select v-model="year" :items="yearsList" label="Ano" outlined dense hide-details
                        class="rounded-lg bg-surface ml-2" style="width: 120px"></v-select>
                </div>
            </v-col>
        </v-row>

        <!-- Tabs Section -->
        <v-row v-if="tabs.length > 1" class="mb-4">
            <v-col cols="12">
                <v-tabs v-model="activeTab" background-color="transparent" color="primary">
                    <v-tab v-for="tab in tabs" :key="tab.id">{{ tab.title }}</v-tab>
                </v-tabs>
            </v-col>
        </v-row>

        <v-row v-if="!loading && currentDashboardData">
            <!-- Section 1: Daily Average (Highlight) -->
            <v-col id="tour-daily-summary-section" cols="12" class="mb-2">
                <span class="text-subtitle-1 font-weight-bold text--secondary">Resumo Diário</span>
            </v-col>

            <v-col cols="12" md="3">
                <v-card class="pa-4 rounded-lg fill-height d-flex flex-column justify-center" elevation="2"
                    color="surface">
                    <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2 pa-2 rounded-lg"
                            style="background-color: #F4F6FA;">mdi-cash-multiple</v-icon>
                        <div class="d-flex align-center">
                            <span class="text-caption text--secondary mr-1">Média Diária (Receitas)</span>
                             <v-tooltip bottom max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <span>Média do valor total recebido por dia no mês selecionado. (Total Recebido / Dias do Mês)</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div>
                        <span class="text-h5 font-weight-bold text--primary">{{ currentDailyAverage?.receipts ?
                            formatCurrency(currentDailyAverage.receipts.value) : 'R$ 0,00' }}</span>
                        <div class="d-flex align-center mt-1">
                            <v-icon small :color="currentDailyAverage?.receipts?.growth >= 0 ? 'success' : 'error'">
                                {{ currentDailyAverage?.receipts?.growth >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                            </v-icon>
                            <span :class="currentDailyAverage?.receipts?.growth >= 0 ? 'success--text' : 'error--text'"
                                class="text-caption font-weight-bold ml-1">
                                {{ currentDailyAverage?.receipts?.growth }}%
                            </span>
                            <span class="text-caption text--secondary ml-1">vs mês anterior</span>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="3">
                <v-card class="pa-4 rounded-lg fill-height d-flex flex-column justify-center" elevation="2"
                    color="surface">
                    <div class="d-flex align-center mb-2">
                        <v-icon color="error" class="mr-2 pa-2 rounded-lg"
                            style="background-color: #FFF4F4;">mdi-cash-minus</v-icon>
                        <div class="d-flex align-center">
                            <span class="text-caption text--secondary mr-1">Média Diária (Despesas)</span>
                            <v-tooltip bottom max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <span>Média do valor de despesas por dia no mês selecionado. (Total Despesas / Dias do Mês)</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div>
                        <span class="text-h5 font-weight-bold text--primary">{{ currentDailyAverage?.expenses ?
                            formatCurrency(currentDailyAverage.expenses.value) : 'R$ 0,00' }}</span>
                        <div class="d-flex align-center mt-1">
                            <v-icon small :color="currentDailyAverage?.expenses?.growth > 0 ? 'error' : 'success'">
                                {{ currentDailyAverage?.expenses?.growth > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                            </v-icon>
                            <span :class="currentDailyAverage?.expenses?.growth > 0 ? 'error--text' : 'success--text'"
                                class="text-caption font-weight-bold ml-1">
                                {{ currentDailyAverage?.expenses?.growth }}%
                            </span>
                            <span class="text-caption text--secondary ml-1">vs mês anterior</span>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="3">
                <v-card class="pa-4 rounded-lg fill-height d-flex flex-column justify-center" elevation="2"
                    color="surface">
                    <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2 pa-2 rounded-lg"
                            style="background-color: #F4F6FA;">mdi-clock-outline</v-icon>
                         <div class="d-flex align-center">
                            <span class="text-caption text--secondary mr-1">Média Diária (Horas/Atend)</span>
                            <v-tooltip bottom max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <span>Média de horas trabalhadas por dia. (Total Horas / Dias Trabalhados)</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div>
                        <span class="text-h5 font-weight-bold text--primary">{{
                            currentDailyAverage?.hours_session_average?.value || '00:00' }}</span>
                        <div class="d-flex align-center mt-1">
                            <v-icon small
                                :color="currentDailyAverage?.hours_session_average?.growth > 0 ? 'success' : 'error'">
                                {{ currentDailyAverage?.hours_session_average?.growth > 0 ? 'mdi-arrow-up' :
                                    'mdi-arrow-down' }}
                            </v-icon>
                            <span
                                :class="currentDailyAverage?.hours_session_average?.growth > 0 ? 'success--text' : 'error--text'"
                                class="text-caption font-weight-bold ml-1">
                                {{ currentDailyAverage?.hours_session_average?.growth }}%
                            </span>
                            <span class="text-caption text--secondary ml-1">vs mês anterior</span>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="3">
                <v-card class="pa-4 rounded-lg fill-height d-flex flex-column justify-center" elevation="2"
                    color="surface">
                    <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2 pa-2 rounded-lg"
                            style="background-color: #F4F6FA;">mdi-account-check-outline</v-icon>
                        <div class="d-flex align-center">
                            <span class="text-caption text--secondary mr-1">Média Diária (Atendimentos)</span>
                             <v-tooltip bottom max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <span>Média de atendimentos realizados por dia. (Total Atendimentos / Dias Trabalhados)</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div>
                        <span class="text-h5 font-weight-bold text--primary">{{
                            currentDailyAverage?.total_sessions_average?.value || '0' }} <span
                                class="text-caption text--secondary">atendimentos</span></span>
                        <div class="d-flex align-center mt-1">
                            <v-icon small
                                :color="currentDailyAverage?.total_sessions_average?.growth > 0 ? 'success' : 'error'">
                                {{ currentDailyAverage?.total_sessions_average?.growth > 0 ? 'mdi-arrow-up' :
                                    'mdi-arrow-down' }}
                            </v-icon>
                            <span
                                :class="currentDailyAverage?.total_sessions_average?.growth > 0 ? 'success--text' : 'error--text'"
                                class="text-caption font-weight-bold ml-1">
                                {{ currentDailyAverage?.total_sessions_average?.growth }}%
                            </span>
                            <span class="text-caption text--secondary ml-1">vs mês anterior</span>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <!-- Add more summary cards or keep empty for spacing -->
            </v-col>


            <!-- Section: Performance (Sessions & Hours) -->
            <v-col cols="12" class="mt-4 mb-2">
                <span class="text-subtitle-1 font-weight-bold text--secondary">Performance & Atendimentos</span>
                <v-divider class="my-2"></v-divider>
            </v-col>

            <v-col cols="12" device="mobile" sm="6" md="4">
                <graphic-card title="Horas Trabalhadas" icon="mdi-clock-outline" color="primary"
                    tooltip-text="Total de horas efetivamente trabalhadas em atendimentos realizados no período."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.worked_minutes, currentDashboardData?.expected_total_session_minutes)) + '% do previsto para o mês'"
                    @click="showListDialog('worked_hours')"
                    :title-value="formatMinutesInTime(currentDashboardData?.worked_hours?.value)"
                    :sub-value="`${currentDashboardData?.work_days?.value} dias trabalhados`"
                    :percentage="currentDashboardData?.worked_hours?.growth"
                    :chart-series="getChartData('worked_hours', 'primary')?.series"
                    :chart-options="getChartData('worked_hours', 'primary')?.chartOptions"
                    :progress-value="calculateProgress(currentDashboardData?.worked_minutes, currentDashboardData?.expected_total_session_minutes)"
                    :progress-total-label="formatMinutesInTime(currentDashboardData?.expected_total_session_minutes)"
                    :loading="loading" :loading-charts="loading_charts"
                    @clickProgress="showListDialog('expected_total_session_minutes')" />
            </v-col>
            <v-col cols="12" device="mobile" sm="6" md="4">
                <graphic-card title="Atendimentos Realizados" icon="mdi-account-check-outline" color="success"
                    tooltip-text="Quantidade total de sessões com status 'realizada' no período."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.total_sessions?.value, currentDashboardData?.expected_total_sessions)) + '% do previsto para o mês'"
                    :title-value="currentDashboardData?.total_sessions?.value"
                    :percentage="currentDashboardData?.total_sessions?.growth"
                    :progress-value="calculateProgress(currentDashboardData?.total_sessions?.value, currentDashboardData?.expected_total_sessions)"
                    :progress-total-label="currentDashboardData?.expected_total_sessions"
                    :chart-options="getChartData('total_sessions', 'success')?.chartOptions"
                    :chart-series="getChartData('total_sessions', 'success')?.series" :loading="loading"
                    :loading-charts="loading_charts" @clickProgress="showListDialog('expected_total_sessions')"
                    @click="showListDialog('total_sessions')" />
            </v-col>
            <v-col cols="12" device="mobile" sm="6" md="4">
                <graphic-card title="Atendimentos Faltosos" icon="mdi-account-cancel-outline" color="error"
                    tooltip-text="Quantidade de sessões onde o paciente não compareceu e não avisou (falta)."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.missed_sessions?.value, currentDashboardData?.expected_total_sessions)) + '% em relação aos atendimentos previstos do mês'"
                    :title-value="currentDashboardData?.missed_sessions?.value"
                    :percentage="currentDashboardData?.missed_sessions?.growth" :is-inverse-trend="true"
                    :chart-options="getChartData('missed_sessions', 'error')?.chartOptions"
                    :chart-series="getChartData('missed_sessions', 'error')?.series"
                    :progress-value="calculateProgress(currentDashboardData?.missed_sessions?.value, currentDashboardData?.expected_total_sessions)"
                    :progress-total-label="currentDashboardData?.expected_total_sessions" :loading="loading"
                    :loading-charts="loading_charts" @click="showListDialog('missed_sessions')"
                    @clickProgress="showListDialog('expected_total_sessions')" />
            </v-col>

            <v-col cols="12" device="mobile" sm="6">
                <graphic-card icon="mdi-calendar-check" title="Taxa de Comparecimento"
                    tooltip-text="Percentual de sessões realizadas em relação ao total agendado."
                    :title-value="currentDashboardData.attendance_rate ? currentDashboardData.attendance_rate + '%' : '0%'"
                    :chart-options="getChartData('attendance_rate', 'green')?.chartOptions"
                    :chart-series="getChartData('attendance_rate', 'green')?.series" color="green" :loading="loading"
                    :loading-charts="loading_charts" />
            </v-col>

            <v-col cols="12" device="mobile" sm="6">
                <graphic-card icon="mdi-calendar-refresh" title="Taxa de Reagendamento"
                    tooltip-text="Percentual de sessões que foram reagendadas em relação ao total."
                    :title-value="currentDashboardData.reschedule_rate ? currentDashboardData.reschedule_rate + '%' : '0%'"
                    :chart-options="getChartData('reschedule_rate', 'warning')?.chartOptions"
                    :chart-series="getChartData('reschedule_rate', 'warning')?.series" color="warning"
                    :loading="loading" :loading-charts="loading_charts" />
            </v-col>

            <v-col cols="12">
                <v-card class="pa-4 rounded-lg" elevation="2" color="surface">
                    <div class="d-flex align-center justify-space-between mb-4">
                        <div class="d-flex align-center">
                            <v-icon color="info" class="mr-2 pa-2 rounded-lg"
                                style="background-color: #F4F6FA;">mdi-chart-line</v-icon>
                             <div class="d-flex align-center">
                                <span class="text-subtitle-1 font-weight-bold text--info mr-1">Taxa de Ocupação da Agenda</span>
                                <v-tooltip bottom max-width="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                    </template>
                                    <span>Porcentagem de horas da agenda ocupadas em relação à capacidade total definida.</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </div>
                    <v-row align="center">
                        <v-col cols="12" md="4">
                            <div class="d-flex flex-column">
                                <span class="text-h4 font-weight-bold info--text">
                                    {{ currentDashboardData?.occupancy_rate?.value + '%' }}
                                </span>
                                <span class="text-caption text--secondary">No mês selecionado</span>
                            </div>
                            <div class="d-flex align-center mt-1">
                                <v-icon small
                                    :color="currentDashboardData?.occupancy_rate?.growth >= 0 ? 'success' : 'error'">
                                    {{ currentDashboardData?.occupancy_rate?.growth >= 0 ? 'mdi-arrow-up' :
                                        'mdi-arrow-down' }}
                                </v-icon>
                                <span
                                    :class="currentDashboardData?.occupancy_rate?.growth >= 0 ? 'success--text' : 'error--text'"
                                    class="text-caption font-weight-bold ml-1">
                                    {{ currentDashboardData?.occupancy_rate?.growth }}%
                                </span>
                                <span class="text-caption text--secondary ml-1">vs mês anterior</span>
                            </div>
                        </v-col>
                        <v-col cols="12" md="8">
                            <!-- Progress bar to show efficiency potentially -->
                            <span class="text-body-2 text--secondary">Baseado em {{
                                currentDashboardData?.occupied_hours?.value }} horas ocupadas e {{
                                    currentDashboardData?.free_hours?.value }} horas livres.</span>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>


            <!-- Section: Financials -->
            <v-col id="tour-financial-section" cols="12" class="mt-6 mb-2">
                <span class="text-subtitle-1 font-weight-bold text--secondary">Financeiro</span>
                <v-divider class="my-2"></v-divider>
            </v-col>

            <v-col id="tour-total-received" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Total Recebido" icon="mdi-cash-plus" color="success"
                    tooltip-text="Soma de todos os valores financeiros recebidos (faturados) no mês."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.total_received?.value, currentDashboardData?.expected_total_received)) + '% do previsto para o mês'"
                    :title-value="formatCurrency(currentDashboardData?.total_received?.value)"
                    :percentage="currentDashboardData?.total_receipts?.growth"
                    :chart-options="getChartData('total_received', 'green')?.chartOptions"
                    :chart-series="getChartData('total_received', 'green')?.series"
                    :progress-value="calculateProgress(currentDashboardData?.total_received?.value, currentDashboardData?.expected_total_received)"
                    :progress-total-label="formatCurrency(currentDashboardData?.expected_total_received)"
                    :loading="loading" :loading-charts="loading_charts" @click="showListDialog('total_received')"
                    @clickProgress="showListDialog('expected_total_received')" />
            </v-col>
            <v-col id="tour-projection" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Projeção Realizada" icon="mdi-cash-check" color="primary"
                    tooltip-text="Soma do valor de todos os atendimentos já realizados, independente se foram pagos ou não."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.total_realized?.value, currentDashboardData?.expected_total_realized)) + '% do previsto para o mês'"
                    :title-value="formatCurrency(currentDashboardData?.total_realized?.value)"
                    :chart-options="getChartData('total_realized', 'primary')?.chartOptions"
                    :chart-series="getChartData('total_realized', 'primary')?.series"
                    :progress-value="calculateProgress(currentDashboardData?.total_realized?.value, currentDashboardData?.expected_total_realized)"
                    :progress-total-label="formatCurrency(currentDashboardData?.expected_total_realized)"
                    :loading="loading" :loading-charts="loading_charts" @click="showListDialog('total_realized')"
                    @clickProgress="showListDialog('expected_total_realized')" />
            </v-col>
            <v-col id="tour-expenses" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Despesas" icon="mdi-cash-remove" color="error"
                    tooltip-text="Soma de todas as despesas lançadas no mês."
                    :description-percent="parseInt(calculateProgress(currentDashboardData?.total_expenses?.value, currentDashboardData?.expected_total_expenses)) + '% do previsto para o mês'"
                    :title-value="formatCurrency(currentDashboardData?.total_expenses?.value)"
                    :chart-options="getChartData('total_expenses', 'error')?.chartOptions"
                    :chart-series="getChartData('total_expenses', 'error')?.series"
                    :progress-value="calculateProgress(currentDashboardData?.total_expenses?.value, currentDashboardData?.expected_total_expenses)"
                    :progress-total-label="formatCurrency(currentDashboardData?.expected_total_expenses)"
                    :loading="loading" :loading-charts="loading_charts" @click="showListDialog('total_expenses')"
                    @clickProgress="showListDialog('expected_total_expenses')" />
            </v-col>
            <v-col id="tour-ticket-average" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Ticket Médio" icon="mdi-ticket-percent-outline" color="secondary"
                    tooltip-text="Valor médio cobrado por atendimento. (Total Faturado / Número de Atendimentos)"
                    :title-value="formatCurrency(currentDashboardData?.average_ticket?.value)"
                    :chart-options="getChartData('average_ticket', 'secondary')?.chartOptions"
                    :chart-series="getChartData('average_ticket', 'secondary')?.series" :loading="loading"
                    :loading-charts="loading_charts" />
            </v-col>

            <v-row class="mt-2 w-100 mx-0">
                <v-col id="tour-effective-hourly-rate" cols="12" md="6" class="pl-md-0">
                    <!-- Hourly Rate Analysis -->
                    <v-card class="pa-4 rounded-lg" elevation="2" color="surface">
                        <div class="d-flex align-center justify-space-between mb-4">
                            <div class="d-flex align-center">
                                <v-icon color="primary" class="mr-2 pa-2 rounded-lg"
                                    style="background-color: #F4F6FA;">mdi-currency-usd</v-icon>
                                <div class="d-flex align-center">
                                    <span class="text-subtitle-1 font-weight-bold text--primary mr-1">Valor Hora Efetivo</span>
                                    <v-tooltip bottom max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                        </template>
                                        <span>Valor efetivo ganho por hora trabalhada. (Total Recebido / Horas Trabalhadas)</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                        <v-row align="center">
                            <v-col cols="12" md="4">
                                <div class="d-flex flex-column">
                                    <span class="text-h4 font-weight-bold primary--text">
                                        {{ formatCurrency(getHourlyRate(currentDashboardData?.total_receipts?.value,
                                            currentDashboardData?.worked_minutes) * 60) }}
                                    </span>
                                    <span class="text-caption text--secondary">Por hora trabalhada</span>
                                </div>
                            </v-col>
                            <v-col cols="12" md="8">
                                <span class="text-body-2 text--secondary">Baseado em {{
                                    formatMinutesInTime(currentDashboardData?.worked_minutes) }} trabalhadas e {{
                                        formatCurrency(currentDashboardData?.total_receipts?.value) }} recebidos.</span>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>

                <v-col id="tour-effective-hourly-rate-realized" cols="12" md="6" class="pr-md-0">
                    <!-- Hourly Rate Analysis (Realized) -->
                    <v-card class="pa-4 rounded-lg" elevation="2" color="surface">
                        <div class="d-flex align-center justify-space-between mb-4">
                            <div class="d-flex align-center">
                                <v-icon color="success" class="mr-2 pa-2 rounded-lg"
                                    style="background-color: #F4F6FA;">mdi-currency-usd</v-icon>
                                <div class="d-flex align-center">
                                    <span class="text-subtitle-1 font-weight-bold success--text mr-1">Valor Hora Realizado</span>
                                    <v-tooltip bottom max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon x-small color="grey lighten-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                        </template>
                                        <span>Valor efetivo da hora considerando os atendimentos já realizados. (Projeção Realizada / Horas Trabalhadas)</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                        <v-row align="center">
                            <v-col cols="12" md="4">
                                <div class="d-flex flex-column">
                                    <span class="text-h4 font-weight-bold success--text">
                                        {{ formatCurrency(getHourlyRate(currentDashboardData?.total_realized?.value,
                                            currentDashboardData?.worked_minutes) * 60) }}
                                    </span>
                                    <span class="text-caption text--secondary">Por hora trabalhada</span>
                                </div>
                            </v-col>
                            <v-col cols="12" md="8">
                                <span class="text-body-2 text--secondary">Baseado em {{
                                    formatMinutesInTime(currentDashboardData?.worked_minutes) }} trabalhadas e {{
                                        formatCurrency(currentDashboardData?.total_realized?.value) }} realizados.</span>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Section: Clients & Growth -->
            <v-col id="tour-clients-section" cols="12" class="mt-6 mb-2">
                <span class="text-subtitle-1 font-weight-bold text--secondary">Clientes & Crescimento</span>
                <v-divider class="my-2"></v-divider>
            </v-col>

            <v-col id="tour-new-contracts" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Novos Contratos" icon="mdi-file-document-edit-outline" color="primary"
                    tooltip-text="Quantidade de novos contratos fechados no mês."
                    :title-value="currentDashboardData?.new_contracts?.value"
                    :percentage="currentDashboardData?.new_contracts?.growth"
                    :chart-options="getChartData('new_contracts', 'primary')?.chartOptions"
                    :chart-series="getChartData('new_contracts', 'primary')?.series" :loading="loading"
                    @click="openDetailsDialog('new_contracts')" />
            </v-col>
            <v-col id="tour-churn-rate" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="Taxa de Churn" icon="mdi-door-open" color="error"
                    tooltip-text="Percentual de clientes que cancelaram o contrato no período."
                    :title-value="`${currentDashboardData?.churn_rate?.value}%`"
                    :percentage="currentDashboardData?.churn_rate?.growth" :is-inverse-trend="true"
                    :chart-options="getChartData('churn_rate', 'error')?.chartOptions"
                    :chart-series="getChartData('churn_rate', 'error')?.series" :loading="loading" @click="openDetailsDialog('churn_rate')" />
            </v-col>
            <v-col id="tour-cac" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="CAC (Custo Aquisição)" icon="mdi-account-plus-outline" color="orange"
                    tooltip-text="Custo Aquisição de Cliente. Valor médio investido para conquistar cada novo cliente."
                    sub-title="Custo Aquisição" :title-value="formatCurrency(currentDashboardData?.cac?.value)"
                    :percentage="currentDashboardData?.cac?.growth" :is-inverse-trend="true"
                    :chart-options="getChartData('cac', 'orange')?.chartOptions"
                    :chart-series="getChartData('cac', 'orange')?.series" @click="openDetailsDialog('cac')" />
            </v-col>
            <v-col id="tour-ltv" cols="12" device="mobile" sm="6" md="3">
                <graphic-card title="LTV (Lifetime Value)" icon="mdi-chart-timeline-variant" color="green"
                    tooltip-text="Lifetime Value. Estimativa do lucro líquido que um cliente gera durante todo o tempo de contrato."
                    sub-title="Lifetime Value" :title-value="formatCurrency(currentDashboardData?.ltv?.value)"
                    :percentage="currentDashboardData?.ltv?.growth"
                    :chart-options="getChartData('ltv', 'green')?.chartOptions"
                    :chart-series="getChartData('ltv', 'green')?.series" @click="openDetailsDialog('ltv')" />
            </v-col>

        </v-row>
        <v-row v-else>
            <!-- Skeleton Loaders for Loading State -->
            <v-col cols="12" md="3" v-for="n in 4" :key="n">
                <v-skeleton-loader type="card" height="150" class="rounded-lg"></v-skeleton-loader>
            </v-col>
            <v-col cols="12" md="4" v-for="n in 3" :key="`g-${n}`">
                <v-skeleton-loader type="card" height="150" class="rounded-lg"></v-skeleton-loader>
            </v-col>
        </v-row>

        <!-- Details Dialog -->
        <v-dialog v-model="show_list_dialog" max-width="1000px" scrollable content-class="rounded-lg">
            <v-card>
                <v-card-title class="pa-4 d-flex justify-space-between align-center border-bottom">
                    <span class="text-h6">Detalhes: {{ type_selected }}</span>
                    <v-btn icon @click="close">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pa-0">
                    <v-data-table :headers="headers" :items="list_items" :single-expand="true"
                        :show-expand="show_expand" item-key="id" class="elevation-0">
                        <!-- Dynamic Slots for Formatting -->
                        <template v-slot:[`item.session_date`]="{ item }">
                            {{ formatDate(item.session_date, true) }}
                        </template>
                        <template v-slot:[`item.extract_date`]="{ item }">
                            {{ formatDate(item.extract_date) }}
                        </template>
                        <template v-slot:[`item.session_value`]="{ item }">
                            {{ formatCurrency(item.session_value) }}
                        </template>
                        <template v-slot:[`item.ammount_paid`]="{ item }">
                            {{ formatCurrency(item.ammount_paid) }}
                        </template>
                        <template v-slot:[`item.total_hours`]="{ item }">
                            {{ formatMinutesInTime(item.total) }}
                        </template>
                        <template v-slot:[`item.attended`]="{ item }">
                            <v-chip small :color="item.attended ? 'green lighten-4' : 'error lighten-4'"
                                :text-color="item.attended ? 'green darken-2' : 'error darken-2'" label>
                                {{ item.attended ? 'Sim' : 'Não' }}
                            </v-chip>
                        </template>
                        <template v-slot:[`item.session_record`]="{ item }">
                            <v-chip small :color="item.session_record ? 'primary lighten-4' : 'grey lighten-3'"
                                :text-color="item.session_record ? 'primary darken-2' : 'grey darken-1'" label>
                                {{ item.session_record ? 'Preenchido' : 'Pendente' }}
                            </v-chip>
                        </template>

                        <!-- Expanded Item -->
                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length" class="pa-4 bg-background">
                                <v-card outlined class="rounded-lg">
                                    <v-data-table :headers="expanded_header" :items="item.items" hide-default-footer
                                        dense>
                                        <template v-slot:[`item.date`]="{ item }">
                                            {{ formatDate(item.date, true) }}
                                        </template>
                                        <template v-slot:[`item.simple_date`]="{ item }">
                                            {{ formatDate(item.date) }}
                                        </template>
                                        <template v-slot:[`item.ammount_paid`]="{ item }">
                                            {{ formatCurrency(item.ammount_paid) }}
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
dayjs.extend(require('dayjs/plugin/duration'));
dayjs.locale('pt-br');

import { mapActions, mapState } from 'vuex';
import GraphicCard from './GraphicCard.vue';

export default {
    components: {
        GraphicCard
    },
    data() {
        return {
            month: dayjs().format('MM'),
            year: dayjs().format('YYYY'),
            month_list: [
                { value: '01', text: 'Janeiro' },
                { value: '02', text: 'Fevereiro' },
                { value: '03', text: 'Março' },
                { value: '04', text: 'Abril' },
                { value: '05', text: 'Maio' },
                { value: '06', text: 'Junho' },
                { value: '07', text: 'Julho' },
                { value: '08', text: 'Agosto' },
                { value: '09', text: 'Setembro' },
                { value: '10', text: 'Outubro' },
                { value: '11', text: 'Novembro' },
                { value: '12', text: 'Dezembro' }
            ],
            loading: true,
            dashboard_data: {}, // Will hold the new nested structure { general: {}, contexts: [] } or old flat structure
            daily_average_data: {}, // Will hold the new nested structure { general: {}, contexts: [] } or old flat structure
            charts_data: {}, // This will likely be merged into dashboard_data in the new structure
            loading_charts: false, // This will likely be merged into 'loading' in the new structure
            loading_daily: false, // This will likely be merged into 'loading' in the new structure
            show_list_dialog: false,
            // Dialog props
            headers: [],
            list_items: [],
            type_selected: "",
            show_expand: false,
            expanded_header: [],
            // Tab state
            activeTab: 0, // 0 for 'Geral', then 1, 2, ... for contexts
            // Helper maps
            description_types: {
                worked_hours: "Horas em Atendimento",
                total_sessions: "Atendimentos Realizados",
                missed_sessions: "Atendimentos em que o cliente faltou",
                hourly_rate: "Valor da Hora",
                total_received: "Total Recebido",
                total_realized: "Total Realizado",
                surplus_value: "Excedente",
                attendance_rate: "Taxa de Comparecimento",
                reschedule_rate: "Taxa de Reagendamento",
                total_expenses: "Despesas",
                average_ticket: "Ticket Médio",
                new_contracts: "Novos Contratos",
                churn_rate: "Taxa de Churn",
                cac: "Custo Aquisição",
                ltv: "Lifetime Value",
                expected_total_session_minutes: "Previsão de Horas",
                expected_total_sessions: "Previsão de Atendimentos",
                expected_total_received: "Previsão de Recebimento",
                expected_total_expenses: "Previsão de Despesas",
                expected_total_realized: "Previsão Realizada"
            }
        }
    },
    computed: {
        yearsList() {
            let current_year = 2023;
            let years = [];
            let now = parseInt(dayjs().format('YYYY'));
            while (current_year <= now) {
                years.push(current_year.toString());
                current_year++;
            }
            return years.sort((a, b) => b - a);
        },
        ...mapState('auth', ['user']),
        userFirstName() {
            if (this.user && this.user.name) {
                return this.user.name.split(' ')[0];
            }
            return 'Doutor(a)';
        },
        greeting() {
            const hour = dayjs().hour();
            if (hour < 12) return 'Bom dia';
            if (hour < 18) return 'Boa tarde';
            return 'Boa noite';
        },
        formattedDate() {
            let d = dayjs().format('dddd, D [de] MMMM [de] YYYY');
            return d.charAt(0).toUpperCase() + d.slice(1);
        },
        currentDashboardData() {
            let source = {};
            if (this.dashboard_data) {
                if (this.dashboard_data.general && this.dashboard_data.contexts) {
                    if (this.activeTab === 0) {
                        source = this.dashboard_data.general || {};
                    } else {
                        const context = this.dashboard_data.contexts[this.activeTab - 1];
                        source = context ? (context.data || {}) : {};
                    }
                } else {
                    source = this.dashboard_data;
                }
            }
            return this.transformData(source);
        },
        currentDailyAverage() {
            let source = {};
            if (this.daily_average_data) {
                if (this.daily_average_data.general && this.daily_average_data.contexts) {
                    if (this.activeTab === 0) {
                        source = this.daily_average_data.general || {};
                    } else {
                        const context = this.daily_average_data.contexts[this.activeTab - 1];
                        source = context ? (context.data || {}) : {};
                    }
                } else {
                    source = this.daily_average_data;
                }
            }
            // Daily average likely uses the same structure or simple objects. 
            // The template expects { receipts: { value, growth }, expenses: { value, growth } ... }
            // If backend is flat, we map it too.
            return this.transformDailyData(source);
        },
        currentChartsData() {
            if (!this.charts_data) return [];

            // New structure: { general: [...], contexts: [...] }
            if (this.charts_data.general && this.charts_data.contexts) {
                if (this.activeTab === 0) {
                    return this.charts_data.general || [];
                }
                // Contexts logic
                // activeTab 1 -> index 0
                const context = this.charts_data.contexts[this.activeTab - 1];
                return context ? (context.data || []) : [];
            }

            // Fallback for old flat structure (object with keys 0..11 or similar)
            // But if it's an object, we want values.
            return Object.values(this.charts_data);
        },
        // Helper to generate tabs for v-tabs
        tabs() {
            const tabs = [{ id: 'general', title: 'Geral' }];
            if (this.dashboard_data && this.dashboard_data.contexts) {
                this.dashboard_data.contexts.forEach(ctx => {
                    tabs.push({ id: ctx.id, title: ctx.title });
                });
            }
            return tabs;
        }
    },
    watch: {
        month() { this.refreshData(); },
        year() { this.refreshData(); },
        activeTab() {
            // When tab changes, if charts_data is still separate, we might need to re-render charts
            // For now, assuming getChartData uses currentDashboardData
        }
    },
    mounted() {
        this.refreshData();
    },
    methods: {
        ...mapActions("dashboard", ["ActionDoGetDashboardDataChartsMonth", "ActionDoGetDashboardDataChartsYear", "ActionDoGetDashboardDailyAverage"]),

        refreshData() {
            this.mountDataMonth();
            this.mountDataYear();
            this.mountDailyAverage();
        },

        mountDataMonth() {
            this.loading = true;
            let start_date = dayjs(`${this.year}-${this.month}-01`);
            this.ActionDoGetDashboardDataChartsMonth({
                start: start_date.format('YYYY-MM-DD'),
                end: start_date.endOf('month').format('YYYY-MM-DD')
            }).then(response => {
                this.dashboard_data = response;
                this.loading = false;
            });
        },
        mountDataYear() {
            this.loading_charts = true;
            this.ActionDoGetDashboardDataChartsYear({ year: this.year }).then(response => {
                this.charts_data = response;
                this.loading_charts = false;
            });
        },
        mountDailyAverage() {
            this.loading_daily = true;
            let start_date = dayjs(`${this.year}-${this.month}-01`);
            this.ActionDoGetDashboardDailyAverage({
                start: start_date.format('YYYY-MM-DD'),
                end: start_date.endOf('month').format('YYYY-MM-DD')
            }).then(response => {
                this.daily_average_data = response;
                this.loading_daily = false;
            });
        },

        // Helper calculations
        calculateProgress(current, total) {
            if (!total || total === 0) return 0;
            return (current * 100) / total;
        },
        calculateHourlyRateProgress() {
            const expectedTotal = this.calculateExpectedHourlyRate();
            if (!expectedTotal) return 0;
            return this.calculateProgress(this.dashboard_data?.hourly_rate, expectedTotal);
        },
        calculateExpectedHourlyRate() {
            const netMinutes = (this.dashboard_data?.expected_total_session_minutes || 0) - (this.dashboard_data?.unworked_minutes || 0);
            if (netMinutes <= 0) return 0;
            const durationHours = dayjs.duration(netMinutes, 'minutes').asHours();
            return (this.dashboard_data?.expected_total_realized || 0) / durationHours;
        },
        formatCurrency(value) {
            if (value === undefined || value === null) return 'R$ 0,00';
            return (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        },
        formatMinutesInTime(total_minutes) {
            if (!total_minutes) return '0h 0m';
            let duration = dayjs.duration(total_minutes, 'minutes');
            let hours = Math.floor(duration.asHours());
            let minutes = duration.minutes();
            return `${hours}h ${minutes}m`;
        },
        formatDate(date, hour = false) {
            if (!date) return '-';
            return dayjs(date).format("DD/MM/YYYY" + (hour ? " HH:mm" : ""));
        },
        getHourlyRate(income, hours) {
            // Check if hours is defined and not zero to avoid division by zero
            // 'worked_hours' value is usually minutes from the API, based on usage in template: getHourlyRate(..., ...worked_hours...value) * 60
            // But wait, the template calls it with `worked_hours.value`.
            // Let's assume income is in cents and hours is minutes.
            if (!hours || hours === 0) return 0;
            // The template multiplies by 60 later: {{ formatCurrency(getHourlyRate(...) * 60) }}
            // So this function should return value per minute? Or value per hour?
            // "Value Hour Effective" usually means Value / Hours.
            // If input is minutes, we divide by (minutes/60).
            // But the template does `* 60` after calling this. 
            // If `getHourlyRate` returned rate per minute, then * 60 gives rate per hour.
            // Let's implement rate per MINUTE here, to align with the * 60 usage.
            // Rate = Income / Minutes.

            return income / hours;
        },

        // Data Transformation Adapters
        transformData(source) {
            if (!source) return {};
            const result = { ...source }; // Keep original keys

            // Helper to create struct
            const createStruct = (key, chartKey = null) => {
                // If chartKey is provided, map that property from history. Otherwise use key.
                const histKey = chartKey || key;

                // Build chart data from history
                const history = this.currentChartsData || [];
                const chartData = history.map(m => m[histKey] !== undefined ? m[histKey] : 0);

                // Map values. Try keys directly, then try mapping if needed.
                const val = source[key];
                const isObj = typeof val === 'object' && val !== null && val.value !== undefined;

                let actualValue = isObj ? val.value : val;
                // Try to parse number if it's a string, to avoid NaN
                if (typeof actualValue === 'string') {
                    // Remove currency symbols if present, though backend usually sends numbers
                    // But if it sends "1200", we want number.
                    // If it sends formatted string, this might be tricky, but usually it's just raw val.
                    let v = parseFloat(actualValue);
                    if (!isNaN(v)) actualValue = v;
                }

                return {
                    value: actualValue,
                    growth: isObj ? (val.growth || 0) : (source[key + '_growth'] || 0),
                    chart_data: chartData
                };
            };

            // Define Mappings (Frontend Key -> Backend Key)
            // Use 'worked_minutes' for both value and chart
            result.worked_hours = createStruct('worked_minutes');
            result.work_days = createStruct('work_days');

            // Mappings for different names
            result.total_sessions = createStruct('total_sessions');
            result.missed_sessions = createStruct('missed_sessions'); // Add this to ensure object structure

            result.total_attendances = createStruct('total_sessions');
            result.attendance_canceled = createStruct('rescheduled_sessions');
            result.total_receipts = createStruct('total_received');

            // Standard Keys
            result.total_expenses = createStruct('total_expenses');
            result.average_ticket = createStruct('average_ticket');

            result.new_contracts = createStruct('new_contracts');
            result.churn_rate = createStruct('churn_rate');
            result.cac = createStruct('cac');
            result.ltv = createStruct('ltv');

            // Keep others
            result.total_received = createStruct('total_received');
            result.total_realized = createStruct('total_realized');
            result.hourly_rate = createStruct('hourly_rate');

            // New Occupancy Fields
            result.occupancy_rate = createStruct('occupancy_rate');
            result.occupied_hours = createStruct('occupied_hours');
            result.free_hours = createStruct('free_hours');

            return result;
        },

        transformDailyData(source) {
            if (!source) return {};

            const createStruct = (key) => {
                const val = source[key];
                // Check if val is already an object { value, growth }
                const isObj = typeof val === 'object' && val !== null && val.value !== undefined;

                let actualValue = isObj ? val.value : val;
                if (typeof actualValue === 'string') {
                    let v = parseFloat(actualValue);
                    if (!isNaN(v)) actualValue = v;
                }

                return {
                    value: actualValue,
                    growth: isObj ? (val.growth || 0) : (source[key + '_growth'] || 0)
                };
            };

            return {
                receipts: createStruct('total_received_average') || { value: 0, growth: 0 },
                expenses: createStruct('total_expenses_average') || { value: 0, growth: 0 },
                hours_session_average: source.hours_session_average,
                total_sessions_average: source.total_sessions_average,
                total_received_average: source.total_received_average
            };
        },

        // Charts
        sanitizeChartData(data) {
            if (!Array.isArray(data)) return [];
            return data.map(val => (val === null || val === undefined) ? 0 : val);
        },

        getChartData(type_data, colorName = "primary") {
            // Extract color hex from Vuetify theme
            const colorHex = this.$vuetify.theme.currentTheme[colorName] || colorName;

            let series_data = [];
            let categories = [];

            const sourceData = this.currentChartsData;
            if (sourceData && sourceData.length) {
                // Assuming sourceData is ordered Jan-Dec or similar, we try to find a month/date field
                // If not present, we might default to standard months if we assume it's valid for the selected year
                const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

                sourceData.forEach((data, index) => {
                    // Try to get month from data.month (integer 1-12) or data.date
                    let label = "";
                    if (data.month) {
                        label = monthNames[parseInt(data.month) - 1] || data.month;
                    } else if (data.date) {
                        label = dayjs(data.date).format("MMM");
                    } else {
                        // Fallback to index based if array length is 12 and fits year
                        label = monthNames[index] || `Mês ${index + 1}`;
                    }
                    categories.push(label);

                    if (type_data === 'worked_hours') {
                        series_data.push(data.worked_minutes || 0);
                    } else {
                        series_data.push(data[type_data] || 0);
                    }
                });
            }

            series_data = this.sanitizeChartData(series_data);

            // Determine Formatter
            let formatter = (val) => val;

            const currencyFields = ['total_received', 'total_expenses', 'total_realized', 'average_ticket', 'cac', 'ltv', 'receipts', 'expenses'];
            const timeFields = ['worked_hours', 'occupied_hours', 'free_hours'];
            const percentFields = ['occupancy_rate', 'churn_rate', 'attendance_rate', 'reschedule_rate'];

            if (currencyFields.includes(type_data)) {
                formatter = (val) => this.formatCurrency(val);
            } else if (timeFields.includes(type_data)) {
                 formatter = (val) => this.formatMinutesInTime(val);
            } else if (percentFields.includes(type_data)) {
                formatter = (val) => val + "%";
            }
            
            // Highlight selected month
            let discreteMarkers = [];
            const selectedMonthInt = parseInt(this.month);
            
            // Find index of selected month in sourceData
             if (sourceData && sourceData.length) {
                sourceData.forEach((data, index) => {
                     let dataMonth = 0;
                     if (data.month) dataMonth = parseInt(data.month);
                     else if (data.date) dataMonth = dayjs(data.date).month() + 1;
                     else dataMonth = index + 1;

                     if (dataMonth === selectedMonthInt) {
                         // Add discrete marker
                         discreteMarkers.push({
                             seriesIndex: 0,
                             dataPointIndex: index,
                             fillColor: colorHex,
                             strokeColor: '#fff',
                             size: 6,
                             shape: "circle"
                         });
                     }
                });
            }

            return {
                series: [{ name: this.description_types[type_data] || type_data, data: series_data }],
                chartOptions: {
                    chart: {
                        type: 'area',
                        height: 100,
                        sparkline: { enabled: true }
                    },
                    markers: {
                        size: 0, // Hide default markers
                        discrete: discreteMarkers, // Show our custom marker
                        hover: { size: 6 }
                    },
                    stroke: { curve: 'smooth', width: 2 },
                    fill: { opacity: 0.3 },
                    colors: [colorHex],
                    labels: categories, // This populates x-axis for tooltip
                    xaxis: {
                        categories: categories,
                        crosshairs: { width: 1 }
                    },
                    tooltip: {
                        fixed: { enabled: false },
                        x: { show: true }, // Show month name
                        y: {
                            formatter: formatter,
                            title: {
                                formatter: (seriesName) => seriesName + ":" // Clean title
                            }
                        },
                        marker: { show: false }
                    }
                }
            };
        },

        // Dialogs
        showListDialog(type_data) {
            this.show_expand = false;
            this.expanded_header = [];
            this.type_selected = this.description_types[type_data] || type_data;

            // Configure headers based on type
            switch (type_data) {
                case "worked_hours":
                case "total_sessions":
                case "missed_sessions":
                case "total_realized":
                    this.headers = [
                        { text: "Cliente", value: "customer.name" },
                        { text: "Data", value: "session_date" },
                        { text: "Duração", value: "session_duration" },
                        { text: "Compareceu", value: "attended", align: 'center' },
                        { text: "Prontuário", value: "session_record", align: 'center' },
                        { text: "Valor", value: "session_value" },
                    ];
                    break;
                case "total_received":
                case "total_expenses":
                    this.headers = [
                        { text: "Data", value: "extract_date", sortable: true },
                        { text: "Valor", value: "ammount_paid", sortable: true },
                        { text: "Título", value: "title", sortable: true },
                        { text: "Descrição", value: "description", sortable: true },
                    ];
                    break;
                case "expected_total_session_minutes":
                    this.headers = [
                        { text: "Cliente", value: "customer_name" },
                        { text: "Total Previsto", value: "total_hours" },
                        { text: '', value: 'data-table-expand' }
                    ];
                    this.show_expand = true;
                    this.expanded_header = [
                        { text: 'Data', value: 'date' },
                        { text: 'Duração', value: 'duration' }
                    ]
                    break;
                case "expected_total_sessions":
                    this.headers = [
                        { text: "Cliente", value: "customer_name" },
                        { text: "Atendimentos", value: "total" },
                        { text: '', value: 'data-table-expand' }
                    ];
                    this.show_expand = true;
                    this.expanded_header = [
                        { text: 'Data', value: 'date' },
                        { text: 'Duração', value: 'duration' }
                    ]
                    break;
                case "expected_total_received":
                    this.headers = [
                        { text: "Cliente", value: "customer_name" },
                        { text: "A Receber", value: "ammount_paid" },
                        { text: '', value: 'data-table-expand' }
                    ];
                    this.show_expand = true;
                    this.expanded_header = [
                        { text: 'Data Pagamento', value: 'date' },
                        { text: 'Valor', value: 'ammount_paid' }
                    ]
                    break;
                case "expected_total_expenses":
                    this.headers = [
                        { text: "Despesa", value: "taxe_title" },
                        { text: "Valor Previsto", value: "ammount_paid" },
                        { text: '', value: 'data-table-expand' }
                    ];
                    this.show_expand = true;
                    this.expanded_header = [
                        { text: 'Data Pagamento', value: 'simple_date' },
                        { text: 'Valor', value: 'ammount_paid' }
                    ]
                    break;
                case "expected_total_realized":
                    this.headers = [
                        { text: "Cliente", value: "customer_name" },
                        { text: "Futuro", value: "ammount_paid" },
                        { text: '', value: 'data-table-expand' }
                    ];
                    this.show_expand = true;
                    this.expanded_header = [
                        { text: 'Data', value: 'date' },
                        { text: 'Valor', value: 'ammount_paid' }
                    ]
                    break;
            }

            if (this.activeTab === 0) {
                this.list_items = this.dashboard_data.general['list_' + type_data] || [];
            } else {
                this.list_items = this.dashboard_data.contexts[this.activeTab - 1]['list_' + type_data] || [];
            }
            this.show_list_dialog = true;
        },
        close() {
            this.show_list_dialog = false;
        }
    }
}
</script>

<style scoped>
.gap-6 {
    gap: 24px;
}

.gap-4 {
    gap: 16px;
}
</style>
